import { dataURLtoFile, uploadImage } from 'src/shared/helpers';

// L'ANTEPRIMA DI UN MATERIALE, COME LA FA IL COMPOSER.
//
// Il giro è quello di `handleMaterialScreenshot` in `ComposerEditorActions.tsx`, tradotto:
//
//   1. `sceneEditor.getMaterialScreenshotAsync(id, w, h)` mette il materiale su una sfera e
//      la fotografa — sta su `SceneViewer`, che `SceneEditor` estende. Attorno allo scatto
//      smonta e rimonta l'environment: è la parte che nessuno pensa a fare ed è quella che
//      sporca la foto;
//   2. `dataURLtoFile` + upload su `fileupload/upload`, che risponde con un URL. Nel composer
//      è `assignOptionImage`; qui è `uploadImage` di `src/shared/helpers`, che è la stessa
//      richiesta con lo stesso campo `image` e lo stesso endpoint;
//   3. l'URL si tiene dove sta il resto di ciò che si sa di un materiale.
//
// NIENTE `materialPhotos`: quella collezione, nel composer, è la sorgente degli **Info
// Point** — `refreshInfoPoints()` crea un hotspot su ogni mesh il cui materiale ha una foto
// lì dentro. È un'altra cosa, e scriverci un'anteprima farebbe comparire un punto informativo
// al cliente finale.
//
// L'URL vive in `scene.metadata.materialPreviews[materialID]`, **con la stessa forma che il
// 3D core usa già** per il gruppo di un materiale (`scene.metadata.groups[materialID]`,
// `SceneEditor.setMaterialGroup`). I metadata della scena Babylon viaggiano dentro il modello
// che `updateSceneModel` riesporta al salvataggio: è il motivo per cui i gruppi sopravvivono
// al ricaricamento, e l'anteprima li segue senza toccare né lo schema né il core.

/**
 * IL RIPIEGO QUANDO LA FOTO NON C'È: la stessa sfera finta disegnata sul colore di base. Sta
 * qui e non nei due posti che la usano — la tessera in elenco e il ritratto in testata —
 * perché due ripieghi diversi sarebbero lo stesso materiale con due facce a seconda di dove
 * lo guardi.
 */
export const materialSwatchFallback = (color?: string | null) => `radial-gradient(circle at 32% 28%, #ffffff 0%, ${color || '#c9ced4'} 62%, #7d858d 100%)`;

/** Basta per una tessera di lista; il composer usa 512 perché è un'immagine di prodotto. */
export const MATERIAL_PREVIEW_SIZE = 128;

const PREVIEWS_KEY = 'materialPreviews';

const previewStore = (sceneEditor: any, create = false): Record<string, string> | null => {
    const scene = sceneEditor?.scene;
    if (!scene) return null;

    if (!scene.metadata) {
        if (!create) return null;
        scene.metadata = {};
    }

    if (!scene.metadata[PREVIEWS_KEY]) {
        if (!create) return null;
        scene.metadata[PREVIEWS_KEY] = {};
    }

    return scene.metadata[PREVIEWS_KEY];
};

/** Scatta la foto del materiale sulla sfera e torna una data URL. Non la salva. */
export const captureMaterialPreview = async (sceneEditor: any, materialID: string, size: number = MATERIAL_PREVIEW_SIZE): Promise<string | null> => {
    if (!sceneEditor?.getMaterialScreenshotAsync || !materialID) return null;

    try {
        return (await sceneEditor.getMaterialScreenshotAsync(materialID, size, size)) ?? null;
    } catch (error) {
        console.error('Material preview: screenshot failed', materialID, error);
        return null;
    }
};

/**
 * Carica lo scatto e ne appunta l'URL sulla scena. Torna l'URL, o `null` se non è andata.
 *
 * L'URL entra nella scena viva: diventa definitivo al salvataggio del prodotto, come ogni
 * altra modifica fatta qui dentro.
 */
export const saveMaterialPreview = async (sceneEditor: any, materialID: string, dataUrl: string): Promise<string | null> => {
    if (!materialID || !dataUrl) return null;

    try {
        const url = await uploadImage(dataURLtoFile(dataUrl, `${materialID}-preview.png`));
        if (!url) return null;

        const store = previewStore(sceneEditor, true);
        if (!store) return null;

        store[materialID] = url;
        return url;
    } catch (error) {
        console.error('Material preview: upload failed', materialID, error);
        return null;
    }
};

const yieldToBrowser = () =>
    new Promise<void>((resolve) => {
        const idle = (window as any).requestIdleCallback;
        if (typeof idle === 'function') idle(() => resolve(), { timeout: 200 });
        else setTimeout(resolve, 0);
    });

/**
 * SCATTA E APPUNTA le anteprime dei materiali che gli vengono dati, UNA ALLA VOLTA e con un
 * respiro fra una e l'altra: ogni scatto smonta e rimonta l'environment della scena sul thread
 * principale, due insieme si calpestano e senza pause la pagina resta a scatti. Torna quante ne
 * ha fatte.
 *
 * NON DECIDE CHI: chi chiama sa se sta riempiendo i buchi o rifacendo tutto. `onShot` arriva
 * dopo lo scatto e prima del caricamento, così chi mostra le tessere non aspetta la rete — e
 * arriva anche quando lo scatto non è riuscito, altrimenti chi conta si ferma sul primo che
 * salta.
 */
export const shootPreviews = async (
    sceneEditor: any,
    materials: any[],
    onShot?: (materialID: string, dataUrl: string | null, done: number, total: number) => void
): Promise<number> => {
    const list = (materials ?? []).filter((material: any) => material?.id);
    if (!sceneEditor || list.length === 0) return 0;

    let made = 0;

    for (let index = 0; index < list.length; index++) {
        const materialID = list[index].id;
        const dataUrl = await captureMaterialPreview(sceneEditor, materialID);

        onShot?.(materialID, dataUrl, index + 1, list.length);

        if (dataUrl) {
            await saveMaterialPreview(sceneEditor, materialID, dataUrl);
            made++;
        }

        await yieldToBrowser();
    }

    return made;
};

/** I materiali che una fotografia non ce l'hanno ancora, nell'ordine in cui arrivano. */
export const materialsWithoutPreview = (sceneEditor: any, materials: any[]): any[] =>
    (materials ?? []).filter((material: any) => material?.id && !readMaterialPreview(sceneEditor, material.id));

/** L'anteprima già salvata, se c'è. */
export const readMaterialPreview = (sceneEditor: any, materialID: string): string | null => {
    if (!materialID) return null;
    const url = previewStore(sceneEditor)?.[materialID];
    return typeof url === 'string' && url ? url : null;
};
